import { useMutation } from '@apollo/client/react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import React from 'react';

import {
  AdminCreateClientDocument,
  AdminLaunchUpdateClientDocument,
  Prefix,
} from '../../../graphql/types';
import { useUploadFile } from '../../../hooks/useUploadFile';
import { BORDER_GRAY } from '../../../theme';
import { useClientSetup } from './clientSetup';
import { FieldName } from './FieldNames';
import type { StepProps } from './types';

const REQUIRED = 'Client name and display name are required.';

type UploadedLogo = { key: string; url: string };

const LogoField: React.FC<{
  url: string | undefined;
  uploading: boolean;
  error: string | undefined;
  disabled: boolean;
  onFile: (file: File) => void;
}> = ({ url, uploading, error, disabled, onFile }) => {
  const label = url ? 'Replace logo' : 'Upload logo';
  const input = React.useRef<HTMLInputElement>(null);
  return (
    <Stack spacing={1}>
      <Typography variant="body2" fontWeight={600}>
        Client logo
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center">
        {url && (
          <Box
            component="img"
            src={url}
            alt="Client logo"
            sx={{
              width: 64,
              height: 64,
              objectFit: 'contain',
              border: `1px solid ${BORDER_GRAY}`,
              borderRadius: 1,
            }}
          />
        )}
        {/* A native button, so Enter and Space open the file picker too. */}
        <Button
          variant="outlined"
          size="small"
          disabled={disabled || uploading}
          onClick={() => input.current?.click()}
        >
          {uploading ? 'Uploading…' : label}
        </Button>
        <input
          ref={input}
          hidden
          type="file"
          accept="image/png,image/jpeg,image/webp"
          aria-label={label}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) {
              onFile(file);
            }
          }}
        />
      </Stack>
      <Typography variant="caption" color={error ? 'error' : 'text.disabled'}>
        {error ?? 'PNG, JPG or WebP, up to 5 MB. Square works best.'}
      </Typography>
      <FieldName>clients.logoId</FieldName>
    </Stack>
  );
};

/**
 * Step 1 of a new client. Before the client exists it creates it; afterwards
 * it edits it.
 */
export const BasicsStep: React.FC<StepProps> = ({
  launch,
  readOnly,
  onEdit,
  registerSave,
  registerCreateClient,
}) => {
  const client = launch?.client;
  const setup = useClientSetup(client?.id);
  const [name, setName] = React.useState(client?.name ?? '');
  const [displayName, setDisplayName] = React.useState(
    client?.displayName ?? '',
  );
  const [showErrors, setShowErrors] = React.useState(false);
  const [logo, setLogo] = React.useState<UploadedLogo>();
  const [uploading, setUploading] = React.useState(false);
  const [logoError, setLogoError] = React.useState<string>();
  const [createClient] = useMutation(AdminCreateClientDocument);
  const [updateClient] = useMutation(AdminLaunchUpdateClientDocument);
  const { uploadImage } = useUploadFile();

  const handleLogo = async (file: File) => {
    // Mark the step changed now, so Continue saves (and waits for the upload)
    // instead of skipping a step that was already saved.
    onEdit();
    setUploading(true);
    setLogoError(undefined);
    try {
      setLogo(await uploadImage({ file, prefix: Prefix.Uploads }));
    } catch (error) {
      setLogoError(
        error instanceof Error ? error.message : 'The logo was not uploaded.',
      );
    } finally {
      setUploading(false);
    }
  };

  const getInput = React.useCallback(() => {
    if (uploading) {
      throw new Error('Wait for the logo to finish uploading.');
    }
    if (!name.trim() || !displayName.trim()) {
      setShowErrors(true);
      throw new Error(REQUIRED);
    }
    return {
      name: name.trim(),
      displayName: displayName.trim(),
      ...(logo && { logoId: logo.key }),
    };
  }, [name, displayName, logo, uploading]);

  React.useEffect(() => {
    if (client) {
      if (!registerSave) {
        return;
      }
      registerSave(async () => {
        await updateClient({
          variables: { input: { id: client.id, ...getInput() } },
        });
      });
      return () => registerSave(undefined);
    }
    if (!registerCreateClient) {
      return;
    }
    registerCreateClient(async () => {
      const { data } = await createClient({
        variables: { input: getInput() },
      });
      const id = data?.data.client.id;
      if (!id) {
        throw new Error('The client was not created.');
      }
      return id;
    });
    return () => registerCreateClient(undefined);
  }, [
    client,
    getInput,
    createClient,
    updateClient,
    registerSave,
    registerCreateClient,
  ]);

  const field = (
    id: string,
    label: string,
    helperText: string,
    value: string,
    setValue: (value: string) => void,
  ) => (
    <Box>
      <TextField
        id={id}
        label={label}
        required
        fullWidth
        size="small"
        value={value}
        disabled={readOnly}
        error={showErrors && !value.trim()}
        helperText={
          showErrors && !value.trim() ? `${label} is required.` : helperText
        }
        onChange={(event) => {
          setValue(event.target.value);
          onEdit();
        }}
      />
      <FieldName>clients.{id}</FieldName>
    </Box>
  );

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
        }}
      >
        {field(
          'name',
          'Client name',
          'Internal name used across admin.',
          name,
          setName,
        )}
        {field(
          'displayName',
          'Display name',
          'Shown to members in the app.',
          displayName,
          setDisplayName,
        )}
      </Box>
      <LogoField
        url={logo?.url ?? setup.data?.client?.logo?.url ?? undefined}
        uploading={uploading}
        error={logoError}
        disabled={readOnly}
        onFile={handleLogo}
      />
    </Stack>
  );
};
